// src/components/authority/AuthorityVerifyPage.jsx
// Verification landing page — processes authority claim tokens from email links
//
// URL pattern: /verify-authority/{64-char-hex-token}
// Calls the authority-verify Edge Function, then redirects to the new group

import React, { useState, useEffect } from 'react';
import { useTranslation } from '../../i18n/useTranslation';

const AuthorityVerifyPage = ({ token, onViewGroup }) => {
  const { t } = useTranslation('authority');
  const [status, setStatus] = useState('verifying'); // 'verifying', 'success', 'error'
  const [errorKey, setErrorKey] = useState(null);
  const [communityId, setCommunityId] = useState(null);
  const [authorityName, setAuthorityName] = useState('');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setErrorKey('invalid_token');
      return;
    }

    const verify = async () => {
      try {
        const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
        const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
        const response = await fetch(
          `${supabaseUrl}/functions/v1/authority-verify`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${supabaseAnonKey}`,
              'apikey': supabaseAnonKey,
            },
            body: JSON.stringify({ token }),
          }
        );

        const data = await response.json();

        if (response.ok && data.success) {
          setStatus('success');
          setCommunityId(data.communityId);
          setAuthorityName(data.authorityName || '');
        } else {
          setStatus('error');
          setErrorKey(data.error || 'unknown');
        }
      } catch (err) {
        console.error('Verification error:', err);
        setStatus('error');
        setErrorKey('network');
      }
    };

    verify();
  }, [token]);

  const handleGoToGroup = () => {
    if (communityId && onViewGroup) {
      // Clear the verification URL path
      window.history.replaceState({}, '', '/');
      // Navigate to the new authority group using App.js navigation
      onViewGroup(communityId, authorityName);
    }
  };

  const handleGoHome = () => {
    window.history.replaceState({}, '', '/');
    window.location.reload();
  };

  // Derive the user-facing error message
  const getErrorMessage = () => {
    switch (errorKey) {
      case 'token_expired':
        return t('verify_expired');
      case 'invalid_token':
        return t('verify_invalid');
      case 'claim_not_pending':
        return t('verify_invalid');
      case 'network':
        return t('verify_network_error');
      default:
        return t('verify_invalid');
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f5f5f5',
        fontFamily: 'OpenSauceSans, sans-serif',
        padding: '20px',
      }}
    >
      <div
        style={{
          maxWidth: '480px',
          width: '100%',
          backgroundColor: '#ffffff',
          borderRadius: '12px',
          boxShadow: '0 2px 12px rgba(0,0,0,0.1)',
          overflow: 'hidden',
        }}
      >
        {/* Header bar */}
        <div
          style={{
            padding: '24px 32px',
            backgroundColor: '#024B99',
            textAlign: 'center',
          }}
        >
          <h1
            style={{
              color: '#ffffff',
              margin: 0,
              fontSize: '24px',
              fontWeight: 600,
            }}
          >
            OurNet
          </h1>
        </div>

        {/* Content */}
        <div style={{ padding: '40px 32px', textAlign: 'center' }}>
          {status === 'verifying' && (
            <>
              <div
                style={{
                  width: '40px',
                  height: '40px',
                  border: '3px solid #e0e0e0',
                  borderTopColor: '#024B99',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  margin: '0 auto 24px',
                }}
              />
              <h2
                style={{ color: '#333', fontSize: '20px', margin: '0 0 8px 0' }}
              >
                {t('verify_verifying')}
              </h2>
              <p style={{ color: '#666', fontSize: '15px', margin: 0 }}>
                {t('verify_please_wait')}
              </p>
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </>
          )}

          {status === 'success' && (
            <>
              <div
                style={{
                  width: '56px',
                  height: '56px',
                  borderRadius: '50%',
                  backgroundColor: '#e8f5e9',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 24px',
                  fontSize: '28px',
                }}
              >
                ✓
              </div>
              <h2
                style={{ color: '#333', fontSize: '20px', margin: '0 0 12px 0' }}
              >
                {t('verify_success')}
              </h2>
              {authorityName && (
                <p
                  style={{
                    color: '#555',
                    fontSize: '15px',
                    margin: '0 0 24px 0',
                  }}
                >
                  {authorityName}
                </p>
              )}
              <button
                onClick={handleGoToGroup}
                style={{
                  backgroundColor: '#024B99',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '25px',
                  padding: '12px 32px',
                  fontSize: '15px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontFamily: 'OpenSauceSans, sans-serif',
                }}
              >
                {t('verify_go_to_group')}
              </button>
            </>
          )}

          {status === 'error' && (
            <>
              <div
                style={{
                  width: '56px',
                  height: '56px',
                  borderRadius: '50%',
                  backgroundColor: '#fbe9e7',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 24px',
                  fontSize: '28px',
                  color: '#d32f2f',
                }}
              >
                ✕
              </div>
              <h2
                style={{ color: '#333', fontSize: '20px', margin: '0 0 12px 0' }}
              >
                {getErrorMessage()}
              </h2>
              <button
                onClick={handleGoHome}
                style={{
                  backgroundColor: '#024B99',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '25px',
                  padding: '12px 32px',
                  fontSize: '15px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontFamily: 'OpenSauceSans, sans-serif',
                }}
              >
                {t('verify_go_home')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthorityVerifyPage;
